import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, useDisclosure } from '@chakra-ui/react';
import { getKybRfiByType } from 'api/kyc';
import * as ROUTES from 'constants/routes';
import RFiList from 'modules/kyc/components/RfiList';
import { DEFAULT_PAGE_SIZE, KYB_RFI_TYPE, KYB_TAB_TITLES } from 'modules/kyc/constants/constants';
import CreateKybLinkModal from 'modules/merchantinfo/components/CreateKybLinkModal';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const KYB_TABS = [
  { title: KYB_TAB_TITLES.IN_REVIEW, type: KYB_RFI_TYPE.IN_REVIEW },
  { title: KYB_TAB_TITLES.ACTIVE_KYBS, type: KYB_RFI_TYPE.ACTIVE },
];

const KybRfiSummary = () => {
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const activeTab = Number(queryParams.get('tab')) || 0;

  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const [currentPages, setCurrentPages] = useState({ 0: 1, 1: 1 });
  const [totalCounts, setTotalCounts] = useState({ 0: 0, 1: 0 });
  const [isLastPage, setIsLastPage] = useState({ 0: true, 1: true });

  const globalLinkModal = useDisclosure();

  const currentPage = currentPages[activeTab];

  const fetchTabData = useCallback(async (tabIndex, page) => {
    const type = KYB_TABS[tabIndex]?.type;

    if (!type) return;

    setTabLoading((prev) => ({ ...prev, [tabIndex]: true }));
    setTabError((prev) => ({ ...prev, [tabIndex]: null }));

    try {
      const response = await getKybRfiByType({ type, page, pageSize: DEFAULT_PAGE_SIZE });
      const data = response?.data?.data;

      setTabData((prev) => ({ ...prev, [tabIndex]: data?.rfi_summary_details || [] }));
      setTotalCounts((prev) => ({ ...prev, [tabIndex]: data?.total_count || 0 }));
      setIsLastPage((prev) => ({ ...prev, [tabIndex]: data?.is_last_page ?? true }));
    } catch (err) {
      setTabError((prev) => ({ ...prev, [tabIndex]: err }));
    } finally {
      setTabLoading((prev) => ({ ...prev, [tabIndex]: false }));
    }
  }, []);

  useEffect(() => {
    fetchTabData(activeTab, currentPage);
  }, [activeTab, currentPage, fetchTabData]);

  const handlePageChange = (tabIndex, newPage) => {
    setCurrentPages((prev) => ({ ...prev, [tabIndex]: newPage }));
  };

  const getTotalPages = (tabIndex) => Math.ceil((totalCounts[tabIndex] || 0) / DEFAULT_PAGE_SIZE);

  const handleRowClick = (rfi) => {
    if (!rfi?.kyb_id) return;

    const route = `${ROUTES.KYB_DETAILS_ROUTE}${rfi.kyb_id}`;

    history.push(route, {
      kybId: rfi.kyb_id,
      entityId: rfi.entity_id,
      entityName: rfi.entity_name,
      kybStatus: rfi.status,
    });
  };

  return (
    <Box mt={6} mb={3}>
      <Flex justify='flex-end' mb={3}>
        <Button colorScheme='teal' size='sm' onClick={globalLinkModal.onOpen}>
          Create Link
        </Button>
      </Flex>
      <Tabs
        isFitted
        variant='enclosed-colored'
        isLazy
        index={activeTab}
        onChange={(tab) => {
          queryParams.set('tab', tab);
          history.push({ search: queryParams.toString() });
        }}
      >
        <TabList>
          {KYB_TABS.map(({ title }, index) => (
            <Tab key={index}>{title}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {KYB_TABS.map(({ title }, index) => (
            <TabPanel key={index}>
              <LoadingErrorWrapper
                isLoading={tabLoading[index]}
                errorTitle={`Error fetching ${title}.`}
                error={tabError[index]}
              >
                <RFiList
                  title={title}
                  rfiList={tabData[index] || []}
                  currentPage={currentPages[index]}
                  totalPages={getTotalPages(index)}
                  totalCount={totalCounts[index]}
                  isLastPage={isLastPage[index]}
                  onPageChange={(page) => handlePageChange(index, page)}
                  onRowClick={handleRowClick}
                />
              </LoadingErrorWrapper>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <CreateKybLinkModal isOpen={globalLinkModal.isOpen} onClose={globalLinkModal.onClose} requireEmail={false} />
    </Box>
  );
};

export default KybRfiSummary;
